import React, { useState } from 'react';
import {
  Box,
  Flex,
  Text,
  IconButton,
  CloseButton,
  useDisclosure,
} from '@chakra-ui/react';
import { ChatIcon } from '@chakra-ui/icons';
import { FormattedMessage } from 'react-intl';
import { useSupportChat } from 'components/SupportChat/hooks/useSupportChat';
import MessageList from 'components/SupportChat/MessageList';
import MessageInput from 'components/SupportChat/MessageInput';
import SupportChatErrorBoundary from 'components/SupportChat/SupportChatErrorBoundary';

const SupportChatPanel: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { messages, sending, error, sendMessage, sendAttachment } =
    useSupportChat();

  return (
    // The panel follows the colour mode through tokens (opaque overlay ground,
    // primary text, success-fill header). It used to be a fixed-light island
    // wrapped in <LightMode>; with every colour a token, Chakra's mode-aware
    // parts (the filled input) and the panel ground now switch together.
    <Flex
      direction="column"
      position="fixed"
      bottom={{ base: '0', md: '90px' }}
      right={{ base: '0', md: '24px' }}
      width={{ base: '100vw', md: '360px' }}
      height={{ base: '100vh', md: '480px' }}
      maxH={{ base: '100vh', md: '70vh' }}
      bg="bg.overlay"
      color="text.primary"
      borderRadius={{ base: '0', md: 'xl' }}
      boxShadow="2xl"
      overflow="hidden"
      zIndex={2000}
    >
      <Flex
        align="center"
        justify="space-between"
        px={4}
        py={3}
        bg="status.success"
        color="status.successFg"
      >
        <Box>
          <Text fontWeight="bold" fontSize="md">
            <FormattedMessage id="support.title" />
          </Text>
          <Text fontSize="xs">
            <FormattedMessage id="support.subtitle" />
          </Text>
        </Box>
        <CloseButton onClick={onClose} aria-label="close-support-chat" />
      </Flex>

      <MessageList messages={messages} />

      {error && (
        <Text fontSize="xs" color="status.error" textAlign="center" pb={1}>
          <FormattedMessage id="support.sendError" />
        </Text>
      )}

      <MessageInput
        onSend={sendMessage}
        onSendFile={sendAttachment}
        disabled={sending}
      />
    </Flex>
  );
};

/**
 * Floating support-chat launcher + panel, mounted globally beside the cookie
 * popup. Wrapped in an error boundary so a backend outage or runtime error in
 * the chat never breaks the app shell.
 */
const SupportChatWidget: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [mounted, setMounted] = useState(false);

  const open = () => {
    setMounted(true);
    onOpen();
  };

  return (
    <SupportChatErrorBoundary>
      {isOpen && mounted && <SupportChatPanel onClose={onClose} />}
      <IconButton
        aria-label="open-support-chat"
        icon={<ChatIcon />}
        colorScheme="green"
        isRound
        size="lg"
        position="fixed"
        bottom="24px"
        right="24px"
        boxShadow="lg"
        zIndex={2001}
        onClick={isOpen ? onClose : open}
      />
    </SupportChatErrorBoundary>
  );
};

export default SupportChatWidget;
